import { fromThousandths, isAmountAbove, isDecimalAmount } from '@platform/design-system';
import { SUPPORTED_LOCALES, type Locale } from '@platform/i18n';
import { z } from 'zod';
import { combinationKey, type OptionIndex } from './options.ts';
import { firstVariant, type Product, type ProductVariant } from './product.ts';
import {
  axesOf,
  reconcileVariants,
  effectiveSku,
  emptyVariantRow,
  type OptionAxis,
  type VariantRow,
} from './product-variants.ts';

export const MAX_NAME_LENGTH = 200;
export const MAX_DESCRIPTION_LENGTH = 5000;
export const MAX_CODE_LENGTH = 80;
export const MAX_ALT_LENGTH = 250;
const MAX_WARRANTY_MONTHS = 600;

const localizedText = z.object({
  name: z.string().max(MAX_NAME_LENGTH),
  description: z.string().max(MAX_DESCRIPTION_LENGTH),
});

const variantRow = z.object({
  variantId: z.string().nullable(),
  valueIds: z.array(z.string()),
  valueCodes: z.array(z.string()),
  enabled: z.boolean(),
  sku: z.string().max(MAX_CODE_LENGTH),
  barcode: z.string().max(MAX_CODE_LENGTH),
  price: z.string(),
  cost: z.string(),
});

const mediaEntry = z.object({
  mediaId: z.string(),
  alt: z.string().max(MAX_ALT_LENGTH),
  thumbnail: z.string(),
  placeholder: z.string().nullable(),
});

export type MediaEntry = z.infer<typeof mediaEntry>;

const stockSplit = z.object({ combination: z.string(), quantity: z.string() });

export type StockSplit = z.infer<typeof stockSplit>;

const optionAxis = z.object({ optionId: z.string(), valueIds: z.array(z.string()) });

const MAX_INITIAL_QUANTITY = 100_000;

export const decimalOf = (text: string): string => text.trim().replace(',', '.');

export const DIMENSION_FIELDS = ['lengthCm', 'widthCm', 'heightCm'] as const;

const MILLIMETRES_PER_CENTIMETRE = 10;

const centimetresOf = (millimetres: number | null | undefined): string =>
  millimetres == null ? '' : String(millimetres / MILLIMETRES_PER_CENTIMETRE);

export const millimetresOf = (centimetres: string): number | null =>
  centimetres.trim() === '' ? null : Math.round(Number(decimalOf(centimetres)) * MILLIMETRES_PER_CENTIMETRE);

const isOptionalDecimal = (text: string): boolean => text.trim() === '' || isDecimalAmount(decimalOf(text));

const isWarranty = (text: string): boolean =>
  text.trim() === '' ||
  (/^\d+$/u.test(text.trim()) && Number(text) > 0 && Number(text) <= MAX_WARRANTY_MONTHS);

export type ProductFormError =
  | 'nameRequired'
  | 'priceInvalid'
  | 'regularPriceTooLow'
  | 'priceBelowCost'
  | 'variantRequired'
  | 'referenceRequired'
  | 'weightInvalid'
  | 'dimensionInvalid'
  | 'warrantyInvalid'
  | 'optionValuesRequired'
  | 'quantityInvalid'
  | 'splitMismatch'
  | 'stockRequired';

export type ProductFormRules = { readonly initialStockRequired: boolean };

export const EDITING_RULES: ProductFormRules = { initialStockRequired: false };

const baseSchema = z.object({
  defaultLocale: z.enum(SUPPORTED_LOCALES),
  translations: z.object({ fr: localizedText, ar: localizedText, en: localizedText }),
  reference: z.string().max(MAX_CODE_LENGTH),
  categoryId: z.string(),
  subsidiaryId: z.string(),
  price: z.string(),
  promo: z.boolean(),
  regularPrice: z.string(),
  cost: z.string(),
  perVariantPrice: z.boolean(),
  weightKg: z.string(),
  lengthCm: z.string(),
  widthCm: z.string(),
  heightCm: z.string(),
  warrantyMonths: z.string(),
  options: z.array(optionAxis),
  variants: z.array(variantRow),
  stockTotals: z.record(z.string(), z.string()),
  stockSplits: z.record(z.string(), z.array(stockSplit)),
  thresholds: z.record(z.string(), z.string()),
  media: z.array(mediaEntry),
});

export type ProductFormValues = z.infer<typeof baseSchema>;

export type ProductFormIssue = {
  readonly path: readonly (string | number)[];
  readonly message: ProductFormError;
};

const sold = (values: ProductFormValues) =>
  values.variants.map((variant, index) => ({ variant, index })).filter(({ variant }) => variant.enabled);

const sellingPrices = (values: ProductFormValues): readonly string[] =>
  values.perVariantPrice ? sold(values).map(({ variant }) => variant.price) : [values.price];

export const isPriceBelowCost = (price: string, cost: string): boolean => {
  const [selling, bought] = [decimalOf(price), decimalOf(cost)];
  return isDecimalAmount(selling) && isDecimalAmount(bought) && isAmountAbove(bought, selling);
};

const priceBelowCostIssues = (values: ProductFormValues): readonly ProductFormIssue[] => {
  if (!values.perVariantPrice)
    return isPriceBelowCost(values.price, values.cost)
      ? [{ path: ['price'], message: 'priceBelowCost' }]
      : [];
  return sold(values).flatMap(({ variant, index }) =>
    isPriceBelowCost(variant.price, variant.cost)
      ? [{ path: ['variants', index, 'price'], message: 'priceBelowCost' as const }]
      : [],
  );
};

const priceIssues = (values: ProductFormValues): readonly ProductFormIssue[] => {
  const invalidPrices: ProductFormIssue[] = values.perVariantPrice
    ? sold(values).flatMap(({ variant, index }) =>
        isDecimalAmount(decimalOf(variant.price))
          ? []
          : [{ path: ['variants', index, 'price'], message: 'priceInvalid' as const }],
      )
    : isDecimalAmount(decimalOf(values.price))
      ? []
      : [{ path: ['price'], message: 'priceInvalid' }];
  const cost: ProductFormIssue[] = values.perVariantPrice
    ? sold(values).flatMap(({ variant, index }) =>
        isOptionalDecimal(variant.cost)
          ? []
          : [{ path: ['variants', index, 'cost'], message: 'priceInvalid' as const }],
      )
    : isOptionalDecimal(values.cost)
      ? []
      : [{ path: ['cost'], message: 'priceInvalid' }];
  const belowCost = invalidPrices.length === 0 && cost.length === 0 ? priceBelowCostIssues(values) : [];
  if (!values.promo) return [...invalidPrices, ...cost, ...belowCost];
  const regular = decimalOf(values.regularPrice);
  if (!isDecimalAmount(regular))
    return [...invalidPrices, ...cost, { path: ['regularPrice'], message: 'priceInvalid' }];
  const belowSelling =
    invalidPrices.length === 0 &&
    sellingPrices(values).some((price) => !isAmountAbove(regular, decimalOf(price)));
  return [
    ...invalidPrices,
    ...cost,
    ...belowCost,
    ...(belowSelling ? [{ path: ['regularPrice'], message: 'regularPriceTooLow' as const }] : []),
  ];
};

const isQuantity = (text: string): boolean =>
  text.trim() === '' || (/^\d+$/u.test(text.trim()) && Number(text) <= MAX_INITIAL_QUANTITY);

const variantIssues = (values: ProductFormValues): readonly ProductFormIssue[] => {
  const rows = sold(values);
  if (rows.length === 0) return [{ path: ['variants'], message: 'variantRequired' }];
  const lacksSku = rows.some(({ variant }) => effectiveSku(variant, values.reference) === '');
  return lacksSku ? [{ path: ['reference'], message: 'referenceRequired' }] : [];
};

const depotFieldIssues = (
  field: 'stockTotals' | 'thresholds',
  entries: Readonly<Record<string, string>>,
): readonly ProductFormIssue[] =>
  Object.entries(entries)
    .filter(([, text]) => !isQuantity(text))
    .map(([locationId]) => ({ path: [field, locationId], message: 'quantityInvalid' as const }));

const wholeNumberOf = (text: string | undefined): number =>
  text === undefined || text.trim() === '' ? 0 : Number(text.trim());

const soldCombinations = (values: ProductFormValues): ReadonlySet<string> =>
  new Set(sold(values).map(({ variant }) => combinationKey(variant.valueIds)));

export const knownSplits = (values: ProductFormValues, locationId: string): readonly StockSplit[] => {
  const combinations = soldCombinations(values);
  return (values.stockSplits[locationId] ?? []).filter((split) => combinations.has(split.combination));
};

const splitIssues = (values: ProductFormValues): readonly ProductFormIssue[] =>
  Object.keys(values.stockSplits).flatMap((locationId): ProductFormIssue[] => {
    const splits = knownSplits(values, locationId);
    if (splits.length === 0) return [];
    const invalid = (values.stockSplits[locationId] ?? []).flatMap((split, position) =>
      isQuantity(split.quantity)
        ? []
        : [{ path: ['stockSplits', locationId, position, 'quantity'], message: 'quantityInvalid' as const }],
    );
    if (invalid.length > 0) return invalid;
    const shared = splits.reduce((sum, split) => sum + wholeNumberOf(split.quantity), 0);
    return shared === wholeNumberOf(values.stockTotals[locationId])
      ? []
      : [{ path: ['stockSplits', locationId], message: 'splitMismatch' }];
  });

const unitsEntered = (values: ProductFormValues): number =>
  Object.values(values.stockTotals).reduce((sum, text) => sum + wholeNumberOf(text), 0);

const initialStockIssues = (values: ProductFormValues, rules: ProductFormRules): readonly ProductFormIssue[] =>
  rules.initialStockRequired &&
  depotFieldIssues('stockTotals', values.stockTotals).length === 0 &&
  unitsEntered(values) === 0
    ? [{ path: ['stockTotals'], message: 'stockRequired' }]
    : [];

const optionIssues = (values: ProductFormValues): readonly ProductFormIssue[] =>
  values.options.flatMap((axis, index) =>
    axis.optionId === '' || axis.valueIds.length === 0
      ? [{ path: ['options', index], message: 'optionValuesRequired' as const }]
      : [],
  );

export const productFormIssues = (
  values: ProductFormValues,
  rules: ProductFormRules = EDITING_RULES,
): readonly ProductFormIssue[] => [
  ...(values.translations[values.defaultLocale].name.trim() === ''
    ? [{ path: ['translations', values.defaultLocale, 'name'], message: 'nameRequired' as const }]
    : []),
  ...priceIssues(values),
  ...optionIssues(values),
  ...variantIssues(values),
  ...depotFieldIssues('stockTotals', values.stockTotals),
  ...initialStockIssues(values, rules),
  ...depotFieldIssues('thresholds', values.thresholds),
  ...splitIssues(values),
  ...(isOptionalDecimal(values.weightKg) ? [] : [{ path: ['weightKg'], message: 'weightInvalid' as const }]),
  ...DIMENSION_FIELDS.flatMap((field) =>
    isOptionalDecimal(values[field]) ? [] : [{ path: [field], message: 'dimensionInvalid' as const }],
  ),
  ...(isWarranty(values.warrantyMonths)
    ? []
    : [{ path: ['warrantyMonths'], message: 'warrantyInvalid' as const }]),
];

export const productFormSchema = (rules: ProductFormRules) =>
  baseSchema.superRefine((values, context) => {
    for (const issue of productFormIssues(values, rules))
      context.addIssue({ code: 'custom', path: [...issue.path], message: issue.message });
  });

const differs = (amounts: readonly (string | undefined)[]): boolean => new Set(amounts).size > 1;

const orProductAmount = (own: string, shared: string): string => (own.trim() === '' ? shared : own);

export const variantsPricedLikeProduct = (values: ProductFormValues): ProductFormValues['variants'] =>
  values.variants.map((variant) => ({
    ...variant,
    price: orProductAmount(variant.price, values.price),
    cost: orProductAmount(variant.cost, values.cost),
  }));

const hasPricesPerVariant = (product: Product): boolean =>
  differs(product.variants.map((variant) => variant.price.amount)) ||
  differs(product.variants.map((variant) => variant.costPrice?.amount));

const rowOf = (entry: ProductVariant, index: OptionIndex): VariantRow => ({
  variantId: entry.id,
  valueIds: [...entry.optionValueIds],
  valueCodes: entry.optionValueIds.map(index.valueCode),
  enabled: true,
  sku: entry.sku,
  barcode: entry.barcode ?? '',
  price: entry.price.amount,
  cost: entry.costPrice?.amount ?? '',
});

const axesFromProduct = (product: Product, index: OptionIndex): OptionAxis[] =>
  axesOf(
    product.variants.map((variant) => variant.optionValueIds),
    (valueId) => index.optionOf(valueId)?.id,
  ).sort(
    (left, right) =>
      (index.optionOf(left.valueIds[0] ?? '')?.position ?? 0) -
      (index.optionOf(right.valueIds[0] ?? '')?.position ?? 0),
  );

const rowsOf = (product: Product | null, index: OptionIndex): VariantRow[] => {
  if (product === null || product.variants.length === 0) return [emptyVariantRow()];
  const saved = product.variants.map((entry) => rowOf(entry, index));
  return reconcileVariants(saved, axesFromProduct(product, index), index.valueCode).map((row) =>
    row.variantId === null ? { ...row, enabled: false } : row,
  );
};

export const formFromProduct = (
  product: Product | null,
  defaultLocale: Locale,
  index: OptionIndex,
): ProductFormValues => {
  const variant = product === null ? undefined : firstVariant(product);
  const translation = (locale: Locale) => {
    const found = product?.translations.find((entry) => entry.locale === locale);
    return { name: found?.name ?? '', description: found?.description ?? '' };
  };
  return {
    defaultLocale,
    translations: { fr: translation('fr'), ar: translation('ar'), en: translation('en') },
    reference: product?.reference ?? '',
    categoryId: product?.categoryIds[0] ?? '',
    subsidiaryId: product?.subsidiaryId ?? '',
    price: variant?.price.amount ?? '',
    promo: variant?.compareAtPrice != null,
    regularPrice: variant?.compareAtPrice?.amount ?? '',
    cost: variant?.costPrice?.amount ?? '',
    perVariantPrice: product !== null && hasPricesPerVariant(product),
    weightKg: variant?.weightG == null ? '' : fromThousandths(variant.weightG),
    lengthCm: centimetresOf(variant?.lengthMm),
    widthCm: centimetresOf(variant?.widthMm),
    heightCm: centimetresOf(variant?.heightMm),
    warrantyMonths: product?.warrantyMonths == null ? '' : String(product.warrantyMonths),
    options: product === null ? [] : axesFromProduct(product, index),
    variants: rowsOf(product, index),
    stockTotals: {},
    stockSplits: {},
    thresholds: {},
    media:
      product?.media.map((entry) => ({
        mediaId: entry.mediaId,
        alt: entry.alt ?? '',
        thumbnail: entry.urls.w320,
        placeholder: entry.placeholder,
      })) ?? [],
  };
};
